import { useMemo, useState } from "react";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import {
  Box,
  ColorSwatch,
  Icon,
  ProportionBar,
  Separator,
  Skeleton,
  Text,
  WidgetBleedScroller,
  WidgetInteractive,
  WidgetListHeader,
  useElementSize,
  type Color,
  type ProportionBarSegment,
} from "@salestrak/ui";
import {
  type OutcomeBreakdownRow,
  type OutcomePerformanceData,
  type OutcomePerformanceUserRow,
} from "./use-outcome-performance";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import {
  DISTRIBUTION_TONES,
  TONE_ORDER,
  type ToneKey,
  tileCaption,
  COMPACT_MAX_WIDTH,
  listMinHeight,
  mixFold,
  DISTRIBUTION_BAR_PX,
  TONE_LABELS,
  TONE_COLORS,
  type OutcomeSelection,
  updatesNoun,
} from "./vocabulary";
import { MemberBars, MemberBarsSkeleton, memberColumns } from "./member-bars";
import { OUTCOME_COLUMNS, OutcomeBars, OutcomeBarsSkeleton, outcomeListRows } from "./outcome-bars";

/** The dialog's member list holds this much above the Updates table it
 *  opens — about six rows — so another member is one press away without the
 *  table losing its room. */
const EXPANDED_LIST_MAX_PX = 264;

/** The tones the bar paints, in the legend's order with the exception last. */
function distributionSegments(data: OutcomePerformanceData): {
  segments: ProportionBarSegment[];
  colors: Color[];
} {
  const segments: ProportionBarSegment[] = [];
  const colors: Color[] = [];
  for (const tone of TONE_ORDER) {
    if (data[tone] === 0) continue;
    segments.push({ id: tone, label: TONE_LABELS[tone], value: data[tone] });
    colors.push(TONE_COLORS[tone]);
  }
  return { segments, colors };
}

/**
 * The whole window as one bar — every posted Update in its tone, the
 * outcome-less ones last.
 */
function OutcomeBar({
  data,
  loading,
  focus,
}: {
  data: OutcomePerformanceData;
  loading: boolean;
  focus: ToneKey | null;
}) {
  if (loading) return <Skeleton delayed height={DISTRIBUTION_BAR_PX} width="100%" />;
  const { segments, colors } = distributionSegments(data);
  return (
    <ProportionBar
      segments={segments}
      colorTokens={colors}
      thickness={DISTRIBUTION_BAR_PX}
      focusedId={focus}
      ariaLabel="Update outcomes"
      renderTooltip={(segment) => (
        <WidgetChartTooltip
          label={segment.label}
          details={`${segment.value.toLocaleString()} ${updatesNoun(segment.value)} · ${formatShare(segment.value, data.total)}`}
        />
      )}
    />
  );
}

/** Focus follows a mouse or the keyboard; a touch press opens the tone
 *  instead — there is no hover to preview with on glass. */
function focusHandlers(tone: ToneKey, onFocus: (tone: ToneKey | null) => void) {
  return {
    onPointerEnter: (event: { pointerType: string }) => {
      if (event.pointerType === "mouse") onFocus(tone);
    },
    onPointerLeave: (event: { pointerType: string }) => {
      if (event.pointerType === "mouse") onFocus(null);
    },
    onFocus: () => onFocus(tone),
    onBlur: () => onFocus(null),
  };
}

/**
 * One tone under the bar: its name, its count and share, and the outcome
 * words that make it up — the "why" beside the "how much". A press opens the
 * tone's Updates; hovering it focuses the card on that tone.
 */
function ToneLegendItem({
  tone,
  data,
  loading,
  showWords,
  selected,
  expanded,
  onSelect,
  onFocus,
}: {
  tone: (typeof DISTRIBUTION_TONES)[number];
  data: OutcomePerformanceData;
  loading: boolean;
  /** The outcome words under the figure — dropped where the width or the
   *  height is short, and where the outcome list under the mix says them. */
  showWords: boolean;
  selected: boolean;
  expanded: boolean | undefined;
  onSelect: (tone: ToneKey) => void;
  onFocus: (tone: ToneKey | null) => void;
}) {
  const count = data[tone];
  const pressable = count > 0 && !loading;
  const words = data.outcomes
    .filter((outcome) => outcome.tone === tone)
    .map((outcome) => `${outcome.label} ${outcome.count.toLocaleString()}`)
    .join(" · ");
  return (
    <WidgetInteractive
      as="button"
      type="button"
      direction="column"
      gap="2xs"
      fill="flex"
      minWidth={0}
      marginXNegative="sm"
      paddingX="sm"
      paddingY="xs"
      rounded="sm"
      cursor={pressable ? "pointer" : undefined}
      hoverBackground={pressable ? true : undefined}
      background={selected ? "selected" : undefined}
      disabled={!pressable}
      aria-label={`${selected && !expanded ? "Hide" : "Show"} ${TONE_LABELS[tone]} ${updatesNoun(count)}`}
      onClick={pressable ? () => onSelect(tone) : undefined}
      {...(pressable ? focusHandlers(tone, onFocus) : null)}
    >
      <Box direction="row" align="center" gap="xs" minWidth={0}>
        <ColorSwatch color={TONE_COLORS[tone]} size="xs" rounded="full" />
        <Text size="xs" weight="medium" variant="muted" truncate>
          {TONE_LABELS[tone]}
        </Text>
      </Box>
      {loading ? (
        <Box paddingY="2xs">
          <Skeleton delayed height={12} width={48} />
        </Box>
      ) : (
        // Tight leading: at `lg` the default 28px line put 5px of air above
        // and below an 18px figure, and the three-line tile read loose on the
        // shortest card. The caption shares the leading so the baseline row
        // stays one rung.
        <Box direction="row" align="baseline" gap="xs" minWidth={0}>
          <Text size="lg" weight="semibold" tabular leading="tight" variant={count === 0 ? "faint" : undefined}>
            {count.toLocaleString()}
          </Text>
          {count > 0 ? (
            <Text size="xs" variant="muted" tabular leading="tight">
              {tileCaption(count, data.completed)}
            </Text>
          ) : null}
        </Box>
      )}
      {showWords && words ? (
        <Text size="xs" variant="faint" truncate>
          {words}
        </Text>
      ) : null}
    </WidgetInteractive>
  );
}

/**
 * The Updates posted without an outcome, as one row under the legend. A
 * task, not a tone: it says how many and opens them, so the outcome can be
 * added. Absent while nothing is missing.
 */
function MissingOutcomesRow({
  count,
  selected,
  expanded,
  onClick,
  onFocus,
}: {
  count: number;
  selected: boolean;
  expanded: boolean | undefined;
  onClick: (() => void) | undefined;
  onFocus: (tone: ToneKey | null) => void;
}) {
  const label = `${count.toLocaleString()} ${count === 1 ? "Update has" : "Updates have"} no outcome`;
  return (
    <WidgetInteractive
      as="button"
      type="button"
      direction="row"
      align="center"
      gap="sm"
      marginXNegative="sm"
      paddingX="sm"
      paddingY="xs"
      rounded="sm"
      shrink={false}
      cursor={onClick ? "pointer" : undefined}
      hoverBackground={onClick ? true : undefined}
      background={selected ? "selected" : undefined}
      aria-expanded={onClick ? selected : undefined}
      aria-label={`${selected && !expanded ? "Hide" : "Show"} ${label}`}
      onClick={onClick}
      disabled={!onClick}
      {...(onClick ? focusHandlers("missing", onFocus) : null)}
    >
      <Box direction="row" align="center" gap="xs" fill="flex" minWidth={0}>
        <ColorSwatch color={TONE_COLORS.missing} size="xs" rounded="full" />
        <Text size="xs" weight="medium" truncate>
          {label}
        </Text>
      </Box>
      {onClick ? (
        <Box direction="row" align="center" gap="2xs" shrink={false}>
          <Text size="xs" variant="muted">
            Review
          </Text>
          <Icon name="chevron-right" size="xs" color="faint" aria-hidden />
        </Box>
      ) : null}
    </WidgetInteractive>
  );
}

/**
 * The summary body, top to bottom: the mix (bar, tone legend, the missing
 * row), then a list of what is behind it. On a team's board that is who
 * posted — one bar per member, its LENGTH the member's Updates against the
 * busiest member's and its COLORS how they ended. On a member's own board a
 * member list would restate the header, so the list is the OUTCOMES: one row
 * per word, its bar the word's count against the largest. Hovering a tone
 * focuses every bar on it (and turns the member rate column into that
 * tone's share), so "who hears Not interested" is one gesture. The positive
 * rate itself reads in the card's header beside the Update count
 * (`OutcomeTotals`): a figure of its own here restated the Positive cell.
 *
 * HEIGHT folds it by what fits (`mixFold`, then `listMinHeight` for the
 * list); WIDTH only drops the outcome words and narrows the name column.
 * The legend's words also drop while the outcome list says them. The list
 * is absent while an Updates table is disclosed on the card, so the table
 * gets the height.
 *
 * In the detail dialog the mix stands down: the dialog is two areas, the
 * list you pick from over the Updates the pick opens. A tone opened from
 * the card focuses the list on that tone — its bars lit — so the list still
 * says what the table is.
 */
export function SummaryBody({
  data,
  loading,
  expanded,
  bodyHeight,
  tableOpen,
  showMembers,
  selection,
  onTone,
  onOutcome,
  onMember,
}: {
  data: OutcomePerformanceData;
  loading: boolean;
  expanded: boolean | undefined;
  /** The measured height of the card body this summary sits in; 0 = not yet. */
  bodyHeight: number;
  /** An Updates table is disclosed under the summary. */
  tableOpen: boolean;
  /** The scope lists members at all (not a member's own board). */
  showMembers: boolean;
  selection: OutcomeSelection | null;
  onTone: (tone: ToneKey) => void;
  onOutcome: (row: OutcomeBreakdownRow) => void;
  onMember: (row: OutcomePerformanceUserRow) => void;
}) {
  const [focus, setFocus] = useState<ToneKey | null>(null);
  const selectedTone = selection && "tone" in selection ? selection.tone : undefined;
  const selectedOutcomeId = selection && "outcomeId" in selection ? selection.outcomeId : undefined;
  const selectedActorId = selection && "actorId" in selection ? selection.actorId : undefined;
  // Zero is "not measured yet" and lays out compact, so a first paint never
  // has to step down (compact only thins: words drop, names narrow).
  const { ref, size } = useElementSize<HTMLDivElement>();
  const compact = size.width < COMPACT_MAX_WIDTH;
  const measured = bodyHeight > 0;
  const fold = expanded || !measured ? "full" : mixFold(bodyHeight, data.missing > 0);
  const listRoom = expanded || (measured && bodyHeight >= listMinHeight(data.missing > 0));
  const listFits = expanded || !tableOpen;
  const outcomeRows = useMemo(() => outcomeListRows(data.outcomes), [data.outcomes]);
  const hasMembers = loading || data.users.length > 0;
  const hasOutcomes = loading || outcomeRows.length > 0;
  const showList = data.total > 0 && listRoom && listFits && (showMembers ? hasMembers : hasOutcomes);
  const showTop = !expanded;
  // The dialog has no legend to hover, so the open tone holds the focus.
  const listFocus = focus ?? (expanded ? (selectedTone ?? null) : null);
  const rateTone = listFocus ?? "positive";
  const columns = showMembers ? memberColumns(rateTone) : OUTCOME_COLUMNS;
  const maxTotal = data.users.reduce((max, row) => Math.max(max, row.total), 0);
  return (
    // While an Updates table is open under it the body hugs its content, so
    // the table — not a gap — takes the spare height.
    <Box ref={ref} direction="column" grow={!tableOpen} shrink={tableOpen ? false : undefined} minHeight={0} minWidth={0}>
      {showTop ? (
        <Box direction="column" gap="sm" shrink={false} minWidth={0}>
          <OutcomeBar data={data} loading={loading} focus={focus} />
          {fold === "bar" ? null : (
            <Box direction="row" gap="md" minWidth={0}>
              {DISTRIBUTION_TONES.map((tone) => (
                <ToneLegendItem
                  key={tone}
                  tone={tone}
                  data={data}
                  loading={loading}
                  showWords={fold === "full" && !compact && !(showList && !showMembers)}
                  selected={selectedTone === tone}
                  expanded={expanded}
                  onSelect={onTone}
                  onFocus={setFocus}
                />
              ))}
            </Box>
          )}
          {(fold === "full" || fold === "words-off") && data.missing > 0 ? (
            <MissingOutcomesRow
              count={data.missing}
              selected={selectedTone === "missing"}
              expanded={expanded}
              onClick={loading ? undefined : () => onTone("missing")}
              onFocus={setFocus}
            />
          ) : null}
        </Box>
      ) : null}
      {showList ? (
        <Box
          direction="column"
          grow={!tableOpen}
          minHeight={0}
          maxHeight={tableOpen ? EXPANDED_LIST_MAX_PX : undefined}
        >
          {showTop ? (
            <Box marginY="md" shrink={false}>
              <Separator />
            </Box>
          ) : null}
          <WidgetListHeader identity={showMembers ? "Member" : "Outcome"} columns={columns} caretSlot />
          {/* The scroller bleeds `sm` and pads back in, so the rows' hover
              wash (a negative margin) never scrolls sideways. */}
          <WidgetBleedScroller inset="sm">
            {showMembers ? (
              loading ? (
                <MemberBarsSkeleton
                  rows={data.users}
                  columns={columns}
                  compact={compact}
                  selectedActorId={selectedActorId}
                />
              ) : (
                <MemberBars
                  rows={data.users}
                  columns={columns}
                  compact={compact}
                  maxTotal={maxTotal}
                  focus={listFocus}
                  rateTone={rateTone}
                  onMemberClick={onMember}
                  selectedActorId={selectedActorId}
                />
              )
            ) : loading ? (
              <OutcomeBarsSkeleton rows={outcomeRows} compact={compact} selectedOutcomeId={selectedOutcomeId} />
            ) : (
              <OutcomeBars
                rows={outcomeRows}
                compact={compact}
                completed={data.completed}
                focus={listFocus}
                onOutcomeClick={onOutcome}
                selectedOutcomeId={selectedOutcomeId}
              />
            )}
          </WidgetBleedScroller>
        </Box>
      ) : null}
    </Box>
  );
}
